'use client'
import * as React from 'react'
import { Copy, CheckCheck, Printer, Hash } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

type Goal = 'awareness' | 'ugc' | 'sales' | 'launch'
type Channel = 'Instagram' | 'TikTok' | 'X' | 'Threads' | 'YouTube'
type RightsApproach = 'comment' | 'dm' | 'terms'

const GOALS: { value: Goal; label: string }[] = [
  { value: 'awareness', label: 'Awareness' },
  { value: 'ugc', label: 'UGC collection' },
  { value: 'sales', label: 'Sales' },
  { value: 'launch', label: 'Launch' },
]

const CHANNELS: Channel[] = ['Instagram', 'TikTok', 'X', 'Threads', 'YouTube']

const RIGHTS: { value: RightsApproach; label: string }[] = [
  { value: 'comment', label: 'Comment-to-consent' },
  { value: 'dm', label: 'DM rights request' },
  { value: 'terms', label: 'Terms-link entry' },
]

const GOAL_KPI: Record<Goal, { line: string; kpi: string }> = {
  awareness: {
    line: 'Get the brand in front of new audiences through creator and customer posts.',
    kpi: 'Reach, impressions, and tagged-post volume on the campaign hashtag.',
  },
  ugc: {
    line: 'Collect a marketable library of customer content the team can reuse on-site and in ads.',
    kpi: 'Number of rights-cleared posts captured, plus cost per usable asset.',
  },
  sales: {
    line: 'Turn social proof into checkout by tagging products inside the best entries.',
    kpi: 'Attributed revenue, shoppable-gallery click-through, and conversion rate.',
  },
  launch: {
    line: 'Build a wave of customer and creator posts around a new drop or product.',
    kpi: 'Tagged-post volume in launch week, plus sell-through on the launched SKU.',
  },
}

const CHANNEL_CADENCE: Record<Channel, string> = {
  Instagram: 'Reels + Stories. Pin the hashtag in bio, reshare entries to Stories daily.',
  TikTok: 'Native short-form video. Seed 2-3 creator posts early, then duet/stitch the best entries.',
  X: 'Short clips and image posts. Quote-post strong entries, run a pinned launch thread.',
  Threads: 'Text-led prompts and reposts. Reply to entrants to keep the thread surfaced.',
  YouTube: 'Shorts for entries, one longer hero edit. Link the hashtag in descriptions.',
}

const RIGHTS_FLOW: Record<RightsApproach, { headline: string; how: string; honest: string }> = {
  comment: {
    headline: 'Comment-to-consent',
    how: 'Ask entrants to reply to your rights request comment with an agreed keyword (for example, a branded hashtag or "I agree"). The comment, timestamp, and the original post are your record of permission.',
    honest:
      'A public reply is good-faith consent, not a signed licence. Keep the comment thread and post intact, and stop using content the moment someone asks you to.',
  },
  dm: {
    headline: 'DM rights request',
    how: 'Send each creator a direct message that states how you want to use the content and links to your usage terms. Their reply agreeing is the consent record.',
    honest:
      'DMs can be edited or deleted by the sender, so capture the agreement (screenshot, export, or a tool that logs it) at the moment it is given.',
  },
  terms: {
    headline: 'Terms-link entry',
    how: 'Publish campaign terms at a stable URL and require entrants to use the hashtag to enter, with the post stating that entry grants the usage rights described in the linked terms.',
    honest:
      'Terms only bind people who actually saw and accepted them. Surface the link in every prompt, and re-confirm before high-stakes uses like paid ads.',
  },
}

type Inputs = {
  brand: string
  hashtag: string
  goal: Goal
  channels: Channel[]
  weeks: string
  startDate: string
  endDate: string
  incentive: string
  rights: RightsApproach
}

function normalizeHashtag(raw: string): string {
  const cleaned = raw.trim().replace(/^#+/, '').replace(/\s+/g, '')
  return cleaned ? `#${cleaned}` : ''
}

function buildBrief(input: Inputs) {
  const brand = input.brand.trim() || 'Your brand'
  const tag = normalizeHashtag(input.hashtag) || '#YourCampaign'
  const channels = input.channels.length ? input.channels : (['Instagram', 'TikTok'] as Channel[])
  const goal = GOAL_KPI[input.goal]
  const rights = RIGHTS_FLOW[input.rights]

  const duration = (() => {
    if (input.startDate && input.endDate) return `${input.startDate} to ${input.endDate}`
    const w = input.weeks.trim()
    if (w) return `${w} week${w === '1' ? '' : 's'}`
    return '4 weeks'
  })()

  const overview = `${brand} is running ${tag} across ${channels.join(', ')} to ${goal.line.charAt(0).toLowerCase()}${goal.line.slice(1)} Entrants post with the hashtag; the team curates the strongest entries, clears rights, and reuses them as social proof on-site and in paid.`

  const incentiveText = input.incentive.trim()
    ? input.incentive.trim()
    : 'No prize this round. Feature the best entries on the brand channels and the website as the incentive to take part.'

  const cadence = channels.map((c) => ({ channel: c, line: CHANNEL_CADENCE[c] }))

  return {
    brand,
    tag,
    channels,
    duration,
    goalLine: goal.line,
    kpi: goal.kpi,
    overview,
    incentiveText,
    cadence,
    rights,
    howToEnter: [
      `Post your photo or video on ${channels.join(', ')}.`,
      `Add ${tag} and tag @${brand.replace(/\s+/g, '').toLowerCase()} so the team can find it.`,
      'Keep the post public so it can be reviewed and, with your permission, reshared.',
    ],
    dos: [
      'Reply to every entrant so the hashtag feed stays active.',
      'Clear rights before reusing anything off the original channel.',
      'Credit the creator wherever the content runs.',
    ],
    donts: [
      "Don't repost to ads or the website before consent is on record.",
      "Don't edit an entry in a way the creator would not recognise.",
      "Don't keep running content after someone asks you to stop.",
    ],
  }
}

function briefToMarkdown(b: ReturnType<typeof buildBrief>): string {
  const lines: string[] = []
  lines.push(`# ${b.brand} - ${b.tag} campaign brief`, '')
  lines.push('## Overview', b.overview, '')
  lines.push('## Goal + KPI', `Goal: ${b.goalLine}`, `KPI: ${b.kpi}`, '')
  lines.push('## Channels + posting cadence')
  b.cadence.forEach((c) => lines.push(`- ${c.channel}: ${c.line}`))
  lines.push(`Duration: ${b.duration}`, '')
  lines.push('## The hashtag + how to enter', `Hashtag: ${b.tag}`)
  b.howToEnter.forEach((h, i) => lines.push(`${i + 1}. ${h}`))
  lines.push('')
  lines.push('## Incentive', b.incentiveText, '')
  lines.push('## Rights + consent flow', `Approach: ${b.rights.headline}`, b.rights.how, `Honest note: ${b.rights.honest}`)
  lines.push('Run rights capture through Idukki Rights Management for a permission record and audit trail.', '')
  lines.push('## Do')
  b.dos.forEach((d) => lines.push(`- ${d}`))
  lines.push('', '## Don\'t')
  b.donts.forEach((d) => lines.push(`- ${d}`))
  return lines.join('\n')
}

export function HashtagBriefGenerator() {
  const [brand, setBrand] = React.useState('')
  const [hashtag, setHashtag] = React.useState('')
  const [goal, setGoal] = React.useState<Goal>('ugc')
  const [channels, setChannels] = React.useState<Channel[]>(['Instagram', 'TikTok'])
  const [weeks, setWeeks] = React.useState('4')
  const [startDate, setStartDate] = React.useState('')
  const [endDate, setEndDate] = React.useState('')
  const [incentive, setIncentive] = React.useState('')
  const [rights, setRights] = React.useState<RightsApproach>('comment')
  const [copied, setCopied] = React.useState(false)

  const brief = React.useMemo(
    () => buildBrief({ brand, hashtag, goal, channels, weeks, startDate, endDate, incentive, rights }),
    [brand, hashtag, goal, channels, weeks, startDate, endDate, incentive, rights],
  )

  function toggleChannel(c: Channel) {
    setChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]))
  }

  function onCopy() {
    navigator.clipboard?.writeText(briefToMarkdown(brief)).then(
      () => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      },
      () => {},
    )
  }

  function fillSample() {
    setBrand('Airlift')
    setHashtag('AirliftEveryday')
    setGoal('ugc')
    setChannels(['Instagram', 'TikTok', 'YouTube'])
    setWeeks('6')
    setStartDate('')
    setEndDate('')
    setIncentive('One winner each week gets a $200 store credit; all featured entrants get a 20% code.')
    setRights('comment')
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
      {/* Form */}
      <form
        onSubmit={(e) => e.preventDefault()}
        className="rounded-2xl border bg-card p-5 sm:p-6 print:hidden"
        aria-label="Campaign brief inputs"
      >
        <div className="grid gap-4">
          <Field label="Brand name">
            <Input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Airlift" />
          </Field>

          <Field label="Campaign hashtag">
            <div className="relative">
              <Hash className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input
                value={hashtag}
                onChange={(e) => setHashtag(e.target.value)}
                placeholder="AirliftEveryday"
                className="pl-9"
                aria-describedby="hashtag-hint"
              />
            </div>
            <span id="hashtag-hint" className="mt-1.5 block text-xs text-muted-foreground">
              The # is added for you.
            </span>
          </Field>

          <fieldset>
            <legend className="text-xs font-medium text-foreground">Goal</legend>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {GOALS.map((g) => {
                const active = goal === g.value
                return (
                  <button
                    key={g.value}
                    type="button"
                    onClick={() => setGoal(g.value)}
                    aria-pressed={active}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-safe:duration-150 ${
                      active ? 'border-primary bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {g.label}
                  </button>
                )
              })}
            </div>
          </fieldset>

          <fieldset>
            <legend className="text-xs font-medium text-foreground">Channels</legend>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {CHANNELS.map((c) => {
                const active = channels.includes(c)
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleChannel(c)}
                    aria-pressed={active}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-safe:duration-150 ${
                      active ? 'border-primary bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {c}
                  </button>
                )
              })}
            </div>
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Start date">
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Field>
            <Field label="End date">
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </Field>
          </div>

          <Field label="Or number of weeks">
            <Input
              type="number"
              min={1}
              value={weeks}
              onChange={(e) => setWeeks(e.target.value)}
              placeholder="4"
              aria-describedby="weeks-hint"
            />
            <span id="weeks-hint" className="mt-1.5 block text-xs text-muted-foreground">
              Used when no start and end dates are set.
            </span>
          </Field>

          <Field label="Incentive or prize (optional)">
            <Input
              value={incentive}
              onChange={(e) => setIncentive(e.target.value)}
              placeholder="$200 store credit each week"
            />
          </Field>

          <fieldset>
            <legend className="text-xs font-medium text-foreground">Rights approach</legend>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {RIGHTS.map((r) => {
                const active = rights === r.value
                return (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setRights(r.value)}
                    aria-pressed={active}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-safe:duration-150 ${
                      active ? 'border-primary bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {r.label}
                  </button>
                )
              })}
            </div>
          </fieldset>

          <button
            type="button"
            onClick={fillSample}
            className="text-left text-xs font-medium text-primary underline underline-offset-4 transition-colors hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Fill with a sample
          </button>
        </div>
      </form>

      {/* Output document */}
      <div>
        <div className="mb-3 flex items-center justify-between gap-3 print:hidden">
          <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">Your one-page brief</p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => window.print()}>
              <Printer className="h-3.5 w-3.5" /> Print
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={onCopy} aria-live="polite">
              {copied ? (
                <>
                  <CheckCheck className="h-3.5 w-3.5" /> Copied
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" /> Copy brief
                </>
              )}
            </Button>
          </div>
        </div>

        <article className="rounded-2xl border bg-card p-6 sm:p-8 print:border-0 print:p-0">
          <header className="border-b pb-5">
            <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-primary">Hashtag campaign brief</p>
            <h2 className="mt-2 font-display text-2xl font-semibold text-foreground">
              {brief.brand}
              <span className="text-muted-foreground"> · </span>
              <span className="text-primary">{brief.tag}</span>
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {brief.channels.join(' · ')} · {brief.duration}
            </p>
          </header>

          <Section title="Overview">
            <p className="text-[15px] leading-relaxed text-foreground/90">{brief.overview}</p>
          </Section>

          <Section title="Goal + KPI">
            <p className="text-[15px] leading-relaxed text-foreground/90">{brief.goalLine}</p>
            <p className="mt-2 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">KPI to watch: </span>
              {brief.kpi}
            </p>
          </Section>

          <Section title="Channels + posting cadence">
            <ul className="space-y-2.5">
              {brief.cadence.map((c) => (
                <li key={c.channel} className="text-[15px] leading-relaxed text-foreground/90">
                  <span className="font-medium text-foreground">{c.channel}: </span>
                  {c.line}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-sm text-muted-foreground">Run time: {brief.duration}</p>
          </Section>

          <Section title="The hashtag + how to enter">
            <p className="text-[15px] leading-relaxed text-foreground/90">
              <span className="font-medium text-foreground">Hashtag: </span>
              {brief.tag}
            </p>
            <ol className="mt-2.5 space-y-1.5">
              {brief.howToEnter.map((h, i) => (
                <li key={i} className="flex gap-2.5 text-[15px] leading-relaxed text-foreground/90">
                  <span className="font-display text-sm font-semibold text-primary">{i + 1}</span>
                  <span>{h}</span>
                </li>
              ))}
            </ol>
          </Section>

          <Section title="Incentive">
            <p className="text-[15px] leading-relaxed text-foreground/90">{brief.incentiveText}</p>
          </Section>

          <Section title="Rights + consent flow">
            <p className="text-[15px] leading-relaxed text-foreground/90">
              <span className="font-medium text-foreground">{brief.rights.headline}: </span>
              {brief.rights.how}
            </p>
            <p className="mt-2.5 rounded-xl border border-dashed bg-secondary/40 p-3 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Honest note: </span>
              {brief.rights.honest}
            </p>
            <p className="mt-2.5 text-sm text-muted-foreground">
              Capturing permission through{' '}
              <span className="font-medium text-foreground">Idukki Rights Management</span> gives every cleared post a
              permission record and audit trail, so the content is safe to run on-site and in ads.
            </p>
          </Section>

          <Section title="Do / Don't">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Do</p>
                <ul className="mt-2 space-y-1.5">
                  {brief.dos.map((d, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm leading-relaxed text-foreground/85">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Don&apos;t</p>
                <ul className="mt-2 space-y-1.5">
                  {brief.donts.map((d, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm leading-relaxed text-foreground/85">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-muted-foreground" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Section>
        </article>

        <p className="mt-4 text-center text-xs text-muted-foreground print:hidden">
          Idukki collects the entries this brief generates, clears the rights, and turns them into shoppable galleries.{' '}
          <a href="https://app.idukki.io/register" className="font-medium text-primary underline underline-offset-4">
            Start a free trial
          </a>
          .
        </p>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-foreground">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-5 border-t pt-5 first-of-type:mt-5">
      <h3 className="font-display text-sm font-semibold uppercase tracking-[0.1em] text-foreground">{title}</h3>
      <div className="mt-2.5">{children}</div>
    </section>
  )
}
